"use client";

import type { FragmentType } from "../../../__generated__/gql";
import { graphql, useFragment } from "../../../__generated__/gql";

export const CancellationPolicyContentFragment = graphql(`
  fragment CancellationPolicyContentFragment on CancellationPolicyContent {
    sections {
      conditionLabel
      tiers {
        label
        feeDescription
      }
      noShowDescription
      changeDeadlineDescription
    }
  }
`);

/**
 * サブスクリプション/ビジネス向けのキャンセルポリシー表示コンポーネント
 *
 * バックエンドの cancellationPolicyContent API から受け取ったデータをそのまま表示する。
 * 新旧ルールの出し分けや遠方サイト判定はバックエンド（CancellationPolicyBuilder）が行う。
 */
/** 連続する同額 tier を折りたたみ、最も早い期限のみ表示する */
function collapseTiers<T extends { feeDescription: string }>(
  tiers: readonly T[],
): T[] {
  return tiers.filter(
    (tier, i) =>
      i === 0 || tier.feeDescription !== tiers[i - 1]!.feeDescription,
  );
}

export function SubscriptionCancelFeePolicyArea({
  cancellationPolicy,
}: {
  cancellationPolicy:
    | FragmentType<typeof CancellationPolicyContentFragment>
    | null
    | undefined;
}) {
  const data = useFragment(
    CancellationPolicyContentFragment,
    cancellationPolicy,
  );

  if (!data) {
    return null;
  }

  const hasTiers = data.sections.some((section) => section.tiers.length > 0);

  return (
    <div className="mt-[20px]">
      <h3 className="font-bold">キャンセルポリシー</h3>
      <div className="mt-[12px] text-[14px] leading-[1.8]">
        {hasTiers && (
          <p>
            キャンセルや予約変更により宿泊をとりやめた場合、宿泊日ごとに以下の料金が発生します。
          </p>
        )}
        {data.sections.map((section, i) => (
          <div key={i}>
            {collapseTiers(section.tiers).map((tier, j) => (
              <p key={j}>
                {section.conditionLabel
                  ? `${section.conditionLabel}： ${tier.label} ${tier.feeDescription}`
                  : `${tier.label} ${tier.feeDescription}`}
              </p>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}
